import "./SlateEditorStyles.scss";

import { withListsReact } from "@prezly/slate-lists";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Button, Spinner } from "react-bootstrap";
import type { Descendant } from "slate";
import { createEditor, Node } from "slate";
import { withHistory } from "slate-history";
import { Editable, ReactEditor, Slate, withReact } from "slate-react";

import {
  EditorPopupProvider,
  useEditorPopupContext,
} from "../../contexts/editorPopupsContext";
import { useOrganizationContext } from "../../contexts/organizations/organizationsContext";
import { useForceUpdate } from "../../hooks/useForceUpdate";
import { isJSON } from "../../utils/isJSON";
import { CustomModal } from "../CustomModal";
import { renderElement } from "./elements/renderElement";
import { renderLeaf } from "./elements/renderLeaf";
import { Helpers } from "./helpers";
import { useKeyDown } from "./hooks/useKeyDown";
import { withCustomBrake } from "./plugins/WithCustomBrake";
import { withInline } from "./plugins/WithInline";
import { withListsPlugin } from "./plugins/WithListPlugin";
import { ShortcutList } from "./ShortcutList";
import { Toolbar } from "./Toolbar";

export interface IProps {
  isLoading: boolean;
  onSave?: (value: string) => void;
  defaultValue: Node[] | null;
  accessAllowed: boolean;
  setValue?: (value: Descendant[] | null) => void;
  placeholder?: string;
  inPopup?: boolean;
  inPlayground?: boolean;
  ref?: any;
  isReadOnly?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  focusOnMount?: boolean;
  needUpdate?: boolean;
  setHasChanges?: (value: boolean) => void;
  type?: string;
}

export const SlateEditor: React.FC<IProps> = forwardRef(
  (
    {
      isLoading,
      onSave,
      defaultValue,
      accessAllowed,
      setValue: setExternalValue,
      placeholder,
      inPopup,
      inPlayground,
      isReadOnly,
      onKeyDown,
      focusOnMount,
      needUpdate,
      setHasChanges: setHasChangesProp,
      type,
    },
    forwardRef,
  ) => {
    // TODO: Fix the type of editor
    const editor = useMemo(
      () =>
        withCustomBrake(
          withInline(
            withListsReact(
              withListsPlugin(withHistory(withReact(createEditor() as any))),
            ),
          ),
        ),
      [],
    );
    const forceUpdate = useForceUpdate();
    useImperativeHandle(forwardRef, () => editor, [editor]);
    const [value, setValue] = useState<Descendant[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const { currentOrganization } = useOrganizationContext();

    const onChange = (newVal: any) => {
      setValue(newVal);
      setExternalValue &&
        setExternalValue(Node.string(editor) === "" ? null : newVal);
    };

    const clearHistory = () => {
      editor.history.undos = [];
      editor.history.redos = [];
      setHasChanges(false);
    };

    useEffect(() => {
      if (editor.history.undos.length > 0 || editor.history.redos.length > 0) {
        setHasChanges(true);
      }
    }, [editor.history.undos, editor.history.redos]);

    const saveChanges = () => {
      const res = isJSON(Node.string(editor))
        ? Node.string(editor)
        : Helpers.slate2markdown(value);
      onSave && onSave(res);
    };
    const { setUrl } = useEditorPopupContext();

    const toggleShortcuts = () => {
      setShowShortcuts(!showShortcuts);
    };

    const { keydownHandler } = useKeyDown(editor, { toggleShortcuts, setUrl });

    const closeShortcutList = () => {
      setShowShortcuts(false);
    };

    useEffect(() => {
      if (!isLoading) {
        clearHistory();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    useEffect(() => {
      if (defaultValue && (inPlayground || needUpdate)) {
        if (editor.selection) {
          editor.selection = null;
        }
        editor.children = defaultValue;
        setValue(defaultValue as Descendant[]);
        forceUpdate();
        if (focusOnMount) {
          ReactEditor.focus(editor);
        }
      }
    }, [
      defaultValue,
      inPlayground,
      focusOnMount,
      needUpdate,
      editor,
      forceUpdate,
    ]);

    useEffect(() => {
      if (setHasChangesProp) {
        setHasChangesProp(editor.history.undos.length !== 0);
      }
    }, [editor.history.undos.length, setHasChangesProp]);

    const noSubscriptionError = `Editing cache items is disabled while there is no active subscription for the ${currentOrganization?.name} organization`;

    return (
      <div className="editor-wrapper" id="editor-wrapper">
        <CustomModal
          show={showShortcuts}
          handleClose={closeShortcutList}
          title="Editor shortcuts"
        >
          <ShortcutList />
        </CustomModal>

        <EditorPopupProvider editor={editor} inPopup={!!inPopup}>
          <Slate
            editor={editor}
            initialValue={defaultValue as Descendant[]}
            onChange={onChange}
          >
            {!inPlayground && (
              <Toolbar
                hasChanges={hasChanges}
                inPopup={inPopup}
                withBanner={!accessAllowed}
              />
            )}

            <Editable
              data-testid="editor"
              className={`editor ${type ? "with-type pt-7" : ""}`}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              scrollSelectionIntoView={() => {}}
              onKeyDown={onKeyDown ? onKeyDown : keydownHandler}
              placeholder={placeholder}
              readOnly={isReadOnly}
            />
            {!accessAllowed && (
              <div className="editor__no-subscription">
                {noSubscriptionError}
              </div>
            )}
            {onSave && (
              <div className="mt-5 d-flex justify-content-end gap-5 ">
                <Button
                  onClick={saveChanges}
                  variant="primary"
                  disabled={
                    isLoading ||
                    !accessAllowed ||
                    editor.history.undos.length === 0 ||
                    Node.string(editor) === ""
                  }
                >
                  Save
                  {isLoading && (
                    <Spinner className="ms-2" size="sm" animation="border" />
                  )}
                </Button>
              </div>
            )}
          </Slate>
        </EditorPopupProvider>
      </div>
    );
  },
);
